import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  forwardRef,
  input,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { Subscription } from 'rxjs';
import { MatInputModule } from '@angular/material/input';

type ControlValueType = string | { question: { value: string } };

@Component({
  selector: 'app-editable-field',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    CommonModule,
    MatInputModule,
  ],
  templateUrl: './editable-field.component.html',
  styleUrls: ['./editable-field.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EditableFieldComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditableFieldComponent
  implements OnInit, OnDestroy, ControlValueAccessor
{
  readonly disabled = input<boolean>(false);
  readonly fixedValue = input<string>(); // fixed text for when it's always disabled, do not use together with control input
  readonly control = input<FormControl<ControlValueType>>();
  readonly placeholder = input<string>('');
  readonly isRequired = input<boolean>(false);
  readonly overrides = input<string[]>([]);
  readonly cssClass = input<string>('');

  private _clickOutsideSubscription: Subscription | null = null;

  private readonly _inputElement =
    viewChild.required<ElementRef<HTMLInputElement>>('inputElement');

  public onTouched: () => void = () => {};
  public onChange: (value: ControlValueType) => void = () => {};

  isEditing = signal<boolean>(false);

  public writeValue(value: ControlValueType): void {
    if (this.disabled()) return;
    if (this.control() && value !== this.control()!.value) {
      this.control()!.setValue(value, { emitEvent: false });
    }
  }

  public registerOnChange(fn: (value: ControlValueType) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.control()!.disable() : this.control()!.enable();
  }

  ngOnInit(): void {
    if (!this.disabled() && !this.control())
      throw 'EditableFiedlComponent initialized enabled but without valid control';
    if (this.control()) {
      this.control()!.valueChanges.subscribe(() => {
        if (this.isEditing()) {
          setTimeout(() => this._inputElement()?.nativeElement?.focus());
        }
      });
    }
  }

  ngOnDestroy(): void {
    this._clickOutsideSubscription?.unsubscribe();
  }

  public finishEditing(newValue: string): void {
    if (!this.isEditing()) return;

    this.isEditing.set(false);
    this._clickOutsideSubscription?.unsubscribe();
    this._clickOutsideSubscription = null;

    const currentValue = this.control()!.value;
    if (typeof currentValue === 'object' && currentValue.question) {
      if (currentValue.question.value !== newValue) {
        currentValue.question.value = newValue;
        this.control()!.setValue(currentValue, { emitEvent: false });
        this.onChange(this.control()!.value);
      }
    } else if (currentValue !== newValue) {
      this.control()!.setValue(newValue, { emitEvent: false });
      this.onChange(this.control()!.value);
    }
  }

  public getFormFieldClass(): string {
    const splitClasses = this.overrides().map((override) => {
      return `${override}`;
    });
    return `form-field ${splitClasses.join(' ')}`;
  }

  public getDisplayValue(): string {
    const value = this.control()!.value;
    if (typeof value === 'object' && value && 'question' in value) {
      return value.question?.value || '';
    }
    return value || '';
  }

  public handleEscape(): void {
    const currentValue = this.control()!.value;
    if (
      typeof currentValue === 'object' &&
      currentValue &&
      'question' in currentValue
    ) {
      this.finishEditing(currentValue.question?.value || '');
    } else {
      this.finishEditing(currentValue || '');
    }
  }
}
